import { useState, useRef } from "react";
import { useSelector } from "react-redux";
import service from "../../services/service";
import endpoints from "../../services/endpoints";

const EyeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

const EyeOffIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

const OTP_LENGTH = 4;

const ChangePasswordModal = ({ onClose }) => {
  const userEmail = useSelector((state) => state.auth.user?.email || "");

  const [step, setStep] = useState("send"); // "send" | "otp" | "reset"
  const [loading, setLoading] = useState(false);

  // OTP step
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const inputRefs = useRef([]);

  // Reset step
  const [form, setForm] = useState({ new_password: "", confirm_password: "" });
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // ── Step 1: Send OTP ─────────────────────────────────────────────────────────
  const handleSendOtp = async () => {
    setLoading(true);
    try {
      await service.post(endpoints.changePassword.sendOtp, { email: userEmail });
      setStep("otp");
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2: Verify OTP ───────────────────────────────────────────────────────
  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const updated = [...otp];
    updated[index] = value;
    setOtp(updated);
    if (value && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (!pasted) return;
    e.preventDefault();
    const updated = [...otp];
    pasted.split("").forEach((char, i) => { updated[i] = char; });
    setOtp(updated);
    inputRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const otpValue = otp.join("");
    if (otpValue.length < OTP_LENGTH) return;
    setLoading(true);
    try {
      await service.post(endpoints.changePassword.verifyOtp, { otp: otpValue });
      setStep("reset");
    } finally {
      setLoading(false);
    }
  };

  // ── Step 3: Reset Password ───────────────────────────────────────────────────
  const handleReset = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm_password) return;
    setLoading(true);
    try {
      await service.post(endpoints.changePassword.reset, {
        new_password: form.new_password,
        confirm_password: form.confirm_password,
      });
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const passwordsMatch =
    form.confirm_password && form.new_password === form.confirm_password;

  return (
    <div className="confirm-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="chpwd-modal">
        {/* Header */}
        <div className="chpwd-modal-header">
          <div>
            <h3 className="chpwd-title">Change Password</h3>
            <p className="chpwd-subtitle">
              {step === "send" && "We'll send a 4-digit OTP to your registered email."}
              {step === "otp" && `Enter the OTP sent to ${userEmail}`}
              {step === "reset" && "Set your new password."}
            </p>
          </div>
          <button className="chpwd-close" onClick={onClose}>✕</button>
        </div>

        {/* Step indicators */}
        <div className="chpwd-steps">
          {["send", "otp", "reset"].map((s, i) => (
            <div key={s} className={`chpwd-step ${step === s ? "active" : (
              (step === "otp" && i === 0) || (step === "reset" && i < 2) ? "done" : ""
            )}`}>
              <div className="chpwd-step-dot">{
                (step === "otp" && i === 0) || (step === "reset" && i < 2) ? "✓" : i + 1
              }</div>
              <span>{["Send OTP", "Verify OTP", "New Password"][i]}</span>
            </div>
          ))}
        </div>

        {/* ── Step 1 ── */}
        {step === "send" && (
          <div className="chpwd-body">
            <div className="chpwd-email-box">
              <span className="chpwd-email-label">Email</span>
              <span className="chpwd-email-val">{userEmail}</span>
            </div>
            <button
              className="commonBtn w-100"
              onClick={handleSendOtp}
              disabled={loading}
            >
              {loading ? "Sending OTP..." : "Send OTP"}
            </button>
          </div>
        )}

        {/* ── Step 2 ── */}
        {step === "otp" && (
          <form className="chpwd-body" onSubmit={handleVerifyOtp}>
            <div className="otpEnter" onPaste={handleOtpPaste}>
              {otp.map((digit, i) => (
                <input
                  key={i}
                  ref={(el) => (inputRefs.current[i] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  autoFocus={i === 0}
                  onChange={(e) => handleOtpChange(i, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(i, e)}
                />
              ))}
            </div>
            <button
              type="submit"
              className="commonBtn w-100"
              disabled={loading || otp.join("").length < OTP_LENGTH}
            >
              {loading ? "Verifying..." : "Verify OTP"}
            </button>
            <button
              type="button"
              className="chpwd-resend"
              onClick={() => { setOtp(Array(OTP_LENGTH).fill("")); handleSendOtp(); }}
              disabled={loading}
            >
              Resend OTP
            </button>
          </form>
        )}

        {/* ── Step 3 ── */}
        {step === "reset" && (
          <form className="chpwd-body" onSubmit={handleReset}>
            <div className="chpwd-field">
              <label>New Password</label>
              <div className="chpwd-input-wrap">
                <input
                  type={showNew ? "text" : "password"}
                  placeholder="Enter new password"
                  value={form.new_password}
                  onChange={(e) => setForm((f) => ({ ...f, new_password: e.target.value }))}
                  required
                />
                <button
                  type="button"
                  className="chpwd-eye"
                  onClick={() => setShowNew((v) => !v)}
                >
                  {showNew ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            </div>
            <div className="chpwd-field">
              <label>Confirm Password</label>
              <div className="chpwd-input-wrap">
                <input
                  type={showConfirm ? "text" : "password"}
                  placeholder="Confirm new password"
                  value={form.confirm_password}
                  onChange={(e) => setForm((f) => ({ ...f, confirm_password: e.target.value }))}
                  required
                />
                <button
                  type="button"
                  className="chpwd-eye"
                  onClick={() => setShowConfirm((v) => !v)}
                >
                  {showConfirm ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
              {form.confirm_password && !passwordsMatch && (
                <span className="chpwd-error">Passwords do not match</span>
              )}
            </div>
            <button
              type="submit"
              className="commonBtn w-100"
              disabled={loading || !passwordsMatch || !form.new_password}
            >
              {loading ? "Updating..." : "Update Password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ChangePasswordModal;
